import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { FaGoogle } from '@react-icons/all-files/fa/FaGoogle';

const SignInContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const CustomGoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background-color: white;
  color: #4b5563;
  border: 1px solid #e5e7eb;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background-color: #f3f4f6;
    border-color: #d1d5db;
  }

  &:active {
    background-color: #e5e7eb;
  }
`;

const GoogleIcon = styled(FaGoogle)`
  color: #4285F4;
  font-size: 18px;
`;

interface GoogleSignInProps {
  onSuccess: (response: any) => void;
  onError: () => void;
  user: any;
  onSignOut: () => void;
}

const GoogleSignIn: React.FC<GoogleSignInProps> = ({ onSuccess, onError, user, onSignOut }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Check for stored session on component mount
    const storedSession = localStorage.getItem('googleSession');
    if (storedSession && !user) {
      try {
        const session = JSON.parse(storedSession);
        // Verify the session is still valid
        if (session.expiresAt > Date.now()) {
          onSuccess(session);
        } else {
          // Session expired, clear it
          localStorage.removeItem('googleSession');
        }
      } catch (err) {
        console.error('Error parsing stored session:', err);
        localStorage.removeItem('googleSession');
      }
    }
  }, [onSuccess, user]);

  const handleClick = () => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      console.error('Google Client ID is not configured');
      return;
    }

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin);
    const scope = 'email profile openid';
    const state = Math.random().toString(36).substring(7);
    const nonce = Math.random().toString(36).substring(7);

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      `&response_type=id_token` +
      `&scope=${encodeURIComponent(scope)}` +
      `&state=${encodeURIComponent(state)}` +
      `&nonce=${encodeURIComponent(nonce)}` +
      `&prompt=select_account`;

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      authUrl,
      'Google Sign In',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    if (!popup) {
      console.error('Popup blocked. Please allow popups for this site.');
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        return;
      }

      try {
        if (popup.location.href.includes(window.location.origin)) {
          clearInterval(checkPopup);
          popup.close();

          const hash = popup.location.hash.substring(1);
          const params = new URLSearchParams(hash);
          const idToken = params.get('id_token');
          const returnedState = params.get('state');

          // Verify state to prevent CSRF
          if (returnedState !== state) {
            console.error('State mismatch - possible CSRF attack');
            onError();
            return;
          }

          if (idToken) {
            // Decode the ID token to get user info
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            const session = {
              userInfo: {
                email: payload.email,
                name: payload.name,
                picture: payload.picture
              },
              idToken: idToken,
              expiresAt: payload.exp * 1000 // Convert to milliseconds
            };
            // Store the session
            localStorage.setItem('googleSession', JSON.stringify(session));
            onSuccess(session);
          } else {
            console.error('No ID token received');
            onError();
          }
        }
      } catch (error) {
        // Cross-origin error, ignore
      }
    }, 100);
  };

  if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
    console.error('Google Client ID is not configured. Please check your .env file.');
    return null;
  }

  return (
    <SignInContainer>
      <CustomGoogleButton onClick={handleClick}>
        <GoogleIcon />
        {i18n.language === 'zh' ? '使用 Google 登入' : 'Sign in with Google'}
      </CustomGoogleButton>
    </SignInContainer>
  );
};

export default GoogleSignIn; 